import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { usePreferences } from "@/hooks/usePreferences";
import type { Json } from "@/integrations/supabase/types";

/**
 * Enhanced Analytics Hook for Data Engine v1
 * 
 * Respects user consent preferences:
 * - analytics_opt_in: Controls product analytics events
 * - research_opt_in: Controls aggregated research data (handled server-side)
 * 
 * Key events tracked:
 * - signup_completed
 * - onboarding_completed
 * - workout_started
 * - workout_completed (with duration, sets, exercises)
 * - exercise_added_to_workout
 * - ai_used
 * - upgrade_clicked
 * - premium_activated
 * - not_found (always logged for debugging)
 */

export type AnalyticsEventType = 
  | 'signup_completed'
  | 'onboarding_completed'
  | 'workout_started'
  | 'workout_completed'
  | 'exercise_added_to_workout'
  | 'ai_used'
  | 'upgrade_clicked'
  | 'premium_activated'
  | 'not_found'
  | 'page_view';

interface TrackEventInput {
  eventType: AnalyticsEventType;
  metadata?: Record<string, any>;
  source?: string;
  force?: boolean; // Force log even if opted out (for critical events like not_found)
}

// Sensitive fields to strip from metadata
const SENSITIVE_FIELDS = ['email', 'password', 'token', 'stripe_id', 'card', 'prompt', 'api_key'];

function sanitizeMetadata(metadata: Record<string, any>): Json {
  const sanitized: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(metadata)) {
    // Skip sensitive fields
    if (SENSITIVE_FIELDS.some(f => key.toLowerCase().includes(f))) {
      continue;
    }
    
    // Recursively sanitize objects
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      sanitized[key] = sanitizeMetadata(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized as Json;
}

// Generate a session ID for the current browser session
let sessionId: string | null = null;
function getSessionId(): string {
  if (!sessionId) {
    sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
  }
  return sessionId;
}

export function useAnalytics() {
  const { user } = useAuth();
  const { preferences } = usePreferences();

  const trackEvent = useMutation({
    mutationFn: async ({ eventType, metadata = {}, source, force = false }: TrackEventInput) => {
      // Check consent (except for critical events or forced events)
      const isOptedIn = preferences?.analytics_opt_in !== false;
      const isCriticalEvent = eventType === 'not_found';
      
      if (!isOptedIn && !isCriticalEvent && !force) {
        console.debug(`Analytics: Skipping ${eventType} - user opted out`);
        return;
      }

      // Sanitize metadata
      const safeMetadata = sanitizeMetadata(metadata);

      // Build the event
      const eventData = {
        event_type: eventType,
        metadata: safeMetadata,
        user_id: user?.id || null,
        session_id: getSessionId(),
        route: typeof window !== 'undefined' ? window.location.pathname : null,
        referrer: typeof document !== 'undefined' ? document.referrer || null : null,
        source: source || null,
      };

      const { error } = await supabase
        .from('analytics_events')
        .insert([eventData]);

      if (error) {
        console.error('Failed to track event:', error);
        // Don't throw - analytics should not break the app
      }
    },
  });

  const track = (
    eventType: AnalyticsEventType, 
    metadata?: Record<string, any>,
    options?: { source?: string; force?: boolean }
  ) => {
    trackEvent.mutate({ 
      eventType, 
      metadata, 
      source: options?.source,
      force: options?.force 
    });
  };

  // Convenience methods for common events
  const trackWorkoutStarted = (workoutId: string, templateId?: string) => {
    track('workout_started', { workout_id: workoutId, template_id: templateId });
  };

  const trackWorkoutCompleted = (data: {
    workoutId: string;
    duration: number;
    totalSets: number;
    totalExercises: number;
  }) => {
    track('workout_completed', {
      workout_id: data.workoutId,
      duration: data.duration,
      total_sets: data.totalSets,
      total_exercises: data.totalExercises,
    });
  };

  const trackExerciseAdded = (exerciseId: string, muscleGroup?: string, equipment?: string) => {
    track('exercise_added_to_workout', {
      exercise_id: exerciseId,
      muscle_group: muscleGroup,
      equipment: equipment,
    });
  };

  const trackAIUsed = (feature: string, tokensUsed?: number) => {
    track('ai_used', { feature, tokens_used: tokensUsed });
  };

  const trackUpgradeClicked = (source: string) => {
    track('upgrade_clicked', { source }, { source });
  };

  const trackNotFound = (path: string, referrer?: string) => {
    // Always track 404s regardless of opt-in (for debugging)
    track('not_found', { path, referrer }, { force: true });
  };

  return {
    track,
    trackEvent,
    trackWorkoutStarted,
    trackWorkoutCompleted,
    trackExerciseAdded,
    trackAIUsed,
    trackUpgradeClicked,
    trackNotFound,
    isOptedIn: preferences?.analytics_opt_in !== false,
  };
}
